// ============================================================================
// API — MÓDULO SERVICIOS MUNICIPALES (Ayuntamiento de Totolac)
// baseURL de axiosInstance ya incluye `/api`, por lo que aquí solo usamos
// rutas relativas a `/servicios`.
// Uploads: se envían con FormData + multipart/form-data.
// Imágenes/archivos servidos en `${REACT_APP_API_URL}/uploads/servicios/...`.
// ============================================================================
import axiosInstance from './AxiosInstance';

const BASE = '/servicios';
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const UPLOADS_BASE = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000'}`;

// ─────────────────────────── Tipos base ───────────────────────────

export interface SmArchivo {
  id: number;
  tipoArchivo?: string;
  nombreArchivo: string;
  rutaArchivo: string;
  momento?: 'ANTES' | 'DESPUES' | string;
  comentario?: string;
  createdAt?: string;
}

export interface Camion {
  id: number;
  numeroEconomico: string;
  placas?: string;
  marca?: string;
  modelo?: string;
  anio?: number;
  capacidad?: string;
  descripcion?: string;
  fotografia?: string;
  estadoCamion: string; // Disponible|En ruta|Recolectando|En traslado|Fuera de servicio
  activo?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Ruta {
  id: number;
  codigo?: string;
  nombre: string;
  descripcion?: string;
  diasServicio?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  estatus?: string; // activo|inactivo
  activo?: boolean;
}

export interface RecorridoPersonal {
  id: number;
  recorridoId: number;
  nombre: string;
  rol?: string;
  observaciones?: string;
}

export interface RecorridoCalle {
  id: number;
  recorridoId: number;
  calle: string;
  colonia?: string;
  atendida: boolean;
  horaAtencion?: string;
  latitud?: number;
  longitud?: number;
  observaciones?: string;
}

export interface RecorridoIncidencia {
  id: number;
  recorridoId: number;
  tipo?: string;
  descripcion: string;
  gravedad?: string;
  latitud?: number;
  longitud?: number;
}

export interface Recorrido {
  id: number;
  folio: string;
  fecha: string;
  horaInicio?: string;
  horaTermino?: string;
  camionId: number;
  rutaId?: number;
  operador?: string;
  observaciones?: string;
  estado?: string;
  municipio?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  codigoPostal?: string;
  tipoVialidad?: string;
  vialidad?: string;
  numeroExterior?: string;
  numeroInterior?: string;
  referencias?: string;
  latitud?: number;
  longitud?: number;
  urlMaps?: string;
  estatus: string; // Pendiente|En proceso|Finalizado|Cancelado
  activo?: boolean;
  camion?: Camion;
  ruta?: Ruta;
  personal?: RecorridoPersonal[];
  calles?: RecorridoCalle[];
  incidencias?: RecorridoIncidencia[];
  archivos?: SmArchivo[];
}

export interface FaenaAvance {
  id: number;
  faenaId: number;
  fecha: string;
  estatusAnterior?: string;
  estatusNuevo?: string;
  descripcion: string;
  responsable?: string;
}

export interface Faena {
  id: number;
  folio: string;
  fecha: string;
  tipoFaenaId?: number;
  descripcion?: string;
  prioridad: string; // Baja|Media|Alta
  responsable?: string;
  fechaProgramada?: string;
  fechaEjecutada?: string;
  observaciones?: string;
  solicitanteNombre?: string;
  solicitanteApPaterno?: string;
  solicitanteApMaterno?: string;
  solicitanteTelefono?: string;
  solicitanteCelular?: string;
  solicitanteCorreo?: string;
  estado?: string;
  municipio?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  codigoPostal?: string;
  tipoVialidad?: string;
  vialidad?: string;
  numeroExterior?: string;
  numeroInterior?: string;
  referencias?: string;
  latitud?: number;
  longitud?: number;
  urlMaps?: string;
  estatus: string; // Pendiente|Programada|En proceso|Finalizada|Cancelada
  activo?: boolean;
  tipoFaena?: CatalogoItem;
  avances?: FaenaAvance[];
  archivos?: SmArchivo[];
}

export interface HerramientaMovimiento {
  id: number;
  herramientaId: number;
  tipo: string; // Alta|Baja|Prestamo|Devolucion|Mantenimiento
  cantidad: number;
  existenciaPrevia?: number;
  existenciaNueva?: number;
  fecha: string;
  usuario?: string;
  responsable?: string;
  observaciones?: string;
}

export interface Herramienta {
  id: number;
  sku: string;
  nombre: string;
  categoriaId?: number;
  marca?: string;
  modelo?: string;
  numeroSerie?: string;
  estadoFisico?: string;
  cantidad: number;
  cantidadDisponible: number;
  ubicacion?: string;
  responsable?: string;
  fechaAdquisicion?: string;
  observaciones?: string;
  fotografia?: string;
  estatus?: string;
  activo?: boolean;
  categoria?: CatalogoItem;
  movimientos?: HerramientaMovimiento[];
}

export interface ReporteAlumbrado {
  id: number;
  folio: string;
  fecha: string;
  origenReporte: string; // WhatsApp|Manual|Oficio|PortalCiudadano
  ciudadanoNombre?: string;
  ciudadanoApellidos?: string;
  ciudadanoTelefono?: string;
  ciudadanoCelular?: string;
  ciudadanoCorreo?: string;
  estado?: string;
  municipio?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  codigoPostal?: string;
  tipoVialidad?: string;
  vialidad?: string;
  numeroExterior?: string;
  numeroInterior?: string;
  referencias?: string;
  latitud?: number;
  longitud?: number;
  urlMaps?: string;
  tipo?: string;
  descripcion?: string;
  responsable?: string;
  fechaAtencion?: string;
  actividadesRealizadas?: string;
  estatus: string; // Pendiente|En proceso|Atendido|Cancelado
  activo?: boolean;
  archivos?: SmArchivo[];
}

export interface Panteon {
  id: number;
  nombre: string;
  localidadId?: number;
  localidad?: string;
  observaciones?: string;
  activo?: boolean;
}

export interface LimpiezaPanteonPersonal {
  id: number;
  limpiezaPanteonId: number;
  nombre: string;
  rol?: string;
  observaciones?: string;
}

export interface LimpiezaPanteon {
  id: number;
  folio?: string;
  panteonId: number;
  fecha: string;
  responsable?: string;
  actividades?: string;
  horaInicio?: string;
  horaTermino?: string;
  observaciones?: string;
  localidadId?: number;
  localidad?: string;
  estatus: string; // Pendiente|En proceso|Finalizada|Cancelada
  activo?: boolean;
  panteon?: Panteon;
  personal?: LimpiezaPanteonPersonal[];
  archivos?: SmArchivo[];
}

export interface LimpiezaCamion {
  id: number;
  folio?: string;
  camionId: number;
  fecha: string;
  responsable?: string;
  tipoLimpieza?: string;
  observaciones?: string;
  estatus: string; // Pendiente|En proceso|Finalizada
  activo?: boolean;
  camion?: Camion;
  archivos?: SmArchivo[];
}

export interface DecoracionPersonal {
  id: number;
  decoracionEventoId: number;
  nombre: string;
  rol?: string;
  observaciones?: string;
}

export interface DecoracionActividad {
  id: number;
  decoracionEventoId: number;
  tipo: string; // Instalacion|Mantenimiento|Retiro
  descripcion?: string;
  fecha?: string;
  horaInicio?: string;
  horaTermino?: string;
  responsable?: string;
  estatus: string;
  observaciones?: string;
}

export interface DecoracionEvento {
  id: number;
  folio?: string;
  evento: string;
  fechaInicio?: string;
  fechaTermino?: string;
  responsable?: string;
  descripcion?: string;
  observaciones?: string;
  estado?: string;
  municipio?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  codigoPostal?: string;
  tipoVialidad?: string;
  vialidad?: string;
  numeroExterior?: string;
  numeroInterior?: string;
  referencias?: string;
  latitud?: number;
  longitud?: number;
  urlMaps?: string;
  estatus: string; // Pendiente|Programada|En proceso|Finalizada|Cancelada
  activo?: boolean;
  personal?: DecoracionPersonal[];
  actividades?: DecoracionActividad[];
  archivos?: SmArchivo[];
}

export interface Bitacora {
  id: number;
  folio?: string;
  fecha: string;
  hora?: string;
  area: string; // Recoleccion|Faenas|Herramientas|Alumbrado|Panteones|LimpiezaCamiones|Decoracion
  tipoActividad?: string;
  actividad?: string;
  responsable?: string;
  personal?: string;
  localidadId?: number;
  localidad?: string;
  colonia?: string;
  calle?: string;
  latitud?: number;
  longitud?: number;
  camionId?: number;
  numeroEconomico?: string;
  entidadOrigen: string;
  entidadOrigenId: number;
  estatus: string;
  observaciones?: string;
}

export interface CatalogoItem {
  id: number;
  codigo?: string;
  descripcion?: string; // tipo-faena
  nombre?: string; // categoria-herramienta
  activo?: boolean;
}

// ─────────────────────────── Helpers ───────────────────────────

const get = <T = any>(url: string, params?: any) =>
  axiosInstance.get<T>(url, { params }).then((r) => r.data);
const post = <T = any>(url: string, body?: any) =>
  axiosInstance.post<T>(url, body).then((r) => r.data);
const put = <T = any>(url: string, body?: any) =>
  axiosInstance.put<T>(url, body).then((r) => r.data);
const patch = <T = any>(url: string, body?: any) =>
  axiosInstance.patch<T>(url, body).then((r) => r.data);
const del = <T = any>(url: string) => axiosInstance.delete<T>(url).then((r) => r.data);
const postForm = <T = any>(url: string, fd: FormData) =>
  axiosInstance.post<T>(url, fd, MULTIPART).then((r) => r.data);
const putForm = <T = any>(url: string, fd: FormData) =>
  axiosInstance.put<T>(url, fd, MULTIPART).then((r) => r.data);
const getBlob = (url: string, params?: any) =>
  axiosInstance.get(url, { params, responseType: 'blob' }).then((r) => r.data as Blob);

/** Descarga un PDF (blob) y lo abre en una pestaña nueva. */
export const abrirPdfEnNuevaPestana = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Se libera después para no invalidar la pestaña recién abierta.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// ─────────────────────────── Camiones ───────────────────────────

export const camionesApi = {
  list: (params?: any) => get<Camion[]>(`${BASE}/camiones`, params),
  get: (id: number) => get<Camion>(`${BASE}/camiones/${id}`),
  create: (fd: FormData) => postForm<Camion>(`${BASE}/camiones`, fd),
  update: (id: number, fd: FormData) => putForm<Camion>(`${BASE}/camiones/${id}`, fd),
  cambiarEstado: (id: number, estadoCamion: string) =>
    patch<Camion>(`${BASE}/camiones/${id}/estado`, { estadoCamion }),
  remove: (id: number) => del(`${BASE}/camiones/${id}`),
};

// ─────────────────────────── Rutas ───────────────────────────

export const rutasApi = {
  list: (params?: any) => get<Ruta[]>(`${BASE}/rutas`, params),
  get: (id: number) => get<Ruta>(`${BASE}/rutas/${id}`),
  create: (body: Partial<Ruta>) => post<Ruta>(`${BASE}/rutas`, body),
  update: (id: number, body: Partial<Ruta>) => put<Ruta>(`${BASE}/rutas/${id}`, body),
  remove: (id: number) => del(`${BASE}/rutas/${id}`),
};

// ─────────────────────────── Recorridos ───────────────────────────

export const recorridosApi = {
  list: (params?: any) => get<Recorrido[]>(`${BASE}/recorridos`, params),
  get: (id: number) => get<Recorrido>(`${BASE}/recorridos/${id}`),
  create: (body: Partial<Recorrido>) => post<Recorrido>(`${BASE}/recorridos`, body),
  update: (id: number, body: Partial<Recorrido>) => put<Recorrido>(`${BASE}/recorridos/${id}`, body),
  cerrar: (id: number, body?: any) => patch<Recorrido>(`${BASE}/recorridos/${id}/cerrar`, body || {}),
  remove: (id: number) => del(`${BASE}/recorridos/${id}`),
  // personal
  addPersonal: (id: number, body: Partial<RecorridoPersonal>) =>
    post(`${BASE}/recorridos/${id}/personal`, body),
  removePersonal: (id: number, pid: number) => del(`${BASE}/recorridos/${id}/personal/${pid}`),
  // calles
  addCalle: (id: number, body: Partial<RecorridoCalle>) =>
    post(`${BASE}/recorridos/${id}/calles`, body),
  atenderCalle: (id: number, cid: number, body?: any) =>
    patch(`${BASE}/recorridos/${id}/calles/${cid}/atender`, body || {}),
  removeCalle: (id: number, cid: number) => del(`${BASE}/recorridos/${id}/calles/${cid}`),
  // incidencias
  addIncidencia: (id: number, body: Partial<RecorridoIncidencia>) =>
    post(`${BASE}/recorridos/${id}/incidencias`, body),
  removeIncidencia: (id: number, iid: number) => del(`${BASE}/recorridos/${id}/incidencias/${iid}`),
  // archivos
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/recorridos/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/recorridos/${id}/archivos/${aid}`),
  reportePdf: (params?: any) => getBlob(`${BASE}/recorridos/reporte/pdf`, params),
};

// ─────────────────────────── Faenas ───────────────────────────

export const faenasApi = {
  list: (params?: any) => get<Faena[]>(`${BASE}/faenas`, params),
  get: (id: number) => get<Faena>(`${BASE}/faenas/${id}`),
  create: (body: Partial<Faena>) => post<Faena>(`${BASE}/faenas`, body),
  update: (id: number, body: Partial<Faena>) => put<Faena>(`${BASE}/faenas/${id}`, body),
  remove: (id: number) => del(`${BASE}/faenas/${id}`),
  addAvance: (id: number, body: Partial<FaenaAvance>) => post(`${BASE}/faenas/${id}/avances`, body),
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/faenas/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/faenas/${id}/archivos/${aid}`),
  reportePdf: (params?: any) => getBlob(`${BASE}/faenas/reporte/pdf`, params),
};

// ─────────────────────────── Herramientas ───────────────────────────

export const herramientasApi = {
  list: (params?: any) => get<Herramienta[]>(`${BASE}/herramientas`, params),
  get: (id: number) => get<Herramienta>(`${BASE}/herramientas/${id}`),
  create: (fd: FormData) => postForm<Herramienta>(`${BASE}/herramientas`, fd),
  update: (id: number, fd: FormData) => putForm<Herramienta>(`${BASE}/herramientas/${id}`, fd),
  remove: (id: number) => del(`${BASE}/herramientas/${id}`),
  movimientos: (id: number) => get<HerramientaMovimiento[]>(`${BASE}/herramientas/${id}/movimientos`),
  addMovimiento: (id: number, body: Partial<HerramientaMovimiento>) =>
    post(`${BASE}/herramientas/${id}/movimientos`, body),
  reportes: (tipo: 'general' | 'prestadas' | 'dañadas' | 'mantenimiento') =>
    get(`${BASE}/herramientas/reportes`, { tipo }),
  reportePdf: (params?: any) => getBlob(`${BASE}/herramientas/reporte/pdf`, params),
};

// ─────────────────────────── Alumbrado ───────────────────────────

export const alumbradoApi = {
  list: (params?: any) => get<ReporteAlumbrado[]>(`${BASE}/alumbrado`, params),
  get: (id: number) => get<ReporteAlumbrado>(`${BASE}/alumbrado/${id}`),
  create: (body: Partial<ReporteAlumbrado>) => post<ReporteAlumbrado>(`${BASE}/alumbrado`, body),
  update: (id: number, body: Partial<ReporteAlumbrado>) =>
    put<ReporteAlumbrado>(`${BASE}/alumbrado/${id}`, body),
  remove: (id: number) => del(`${BASE}/alumbrado/${id}`),
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/alumbrado/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/alumbrado/${id}/archivos/${aid}`),
  reportePdf: (params?: any) => getBlob(`${BASE}/alumbrado/reporte/pdf`, params),
};

// ─────────────────────────── Panteones (catálogo) ───────────────────────────

export const panteonesApi = {
  list: (params?: any) => get<Panteon[]>(`${BASE}/panteones`, params),
  get: (id: number) => get<Panteon>(`${BASE}/panteones/${id}`),
  create: (body: Partial<Panteon>) => post<Panteon>(`${BASE}/panteones`, body),
  update: (id: number, body: Partial<Panteon>) => put<Panteon>(`${BASE}/panteones/${id}`, body),
  remove: (id: number) => del(`${BASE}/panteones/${id}`),
};

// ─────────────────────────── Limpieza de panteones ───────────────────────────

export const limpiezaPanteonesApi = {
  list: (params?: any) => get<LimpiezaPanteon[]>(`${BASE}/limpieza-panteones`, params),
  get: (id: number) => get<LimpiezaPanteon>(`${BASE}/limpieza-panteones/${id}`),
  create: (body: Partial<LimpiezaPanteon>) => post<LimpiezaPanteon>(`${BASE}/limpieza-panteones`, body),
  update: (id: number, body: Partial<LimpiezaPanteon>) =>
    put<LimpiezaPanteon>(`${BASE}/limpieza-panteones/${id}`, body),
  remove: (id: number) => del(`${BASE}/limpieza-panteones/${id}`),
  addPersonal: (id: number, body: Partial<LimpiezaPanteonPersonal>) =>
    post(`${BASE}/limpieza-panteones/${id}/personal`, body),
  removePersonal: (id: number, pid: number) =>
    del(`${BASE}/limpieza-panteones/${id}/personal/${pid}`),
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/limpieza-panteones/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/limpieza-panteones/${id}/archivos/${aid}`),
};

// ─────────────────────────── Limpieza de camiones ───────────────────────────

export const limpiezaCamionesApi = {
  list: (params?: any) => get<LimpiezaCamion[]>(`${BASE}/limpieza-camiones`, params),
  get: (id: number) => get<LimpiezaCamion>(`${BASE}/limpieza-camiones/${id}`),
  create: (body: Partial<LimpiezaCamion>) => post<LimpiezaCamion>(`${BASE}/limpieza-camiones`, body),
  update: (id: number, body: Partial<LimpiezaCamion>) =>
    put<LimpiezaCamion>(`${BASE}/limpieza-camiones/${id}`, body),
  remove: (id: number) => del(`${BASE}/limpieza-camiones/${id}`),
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/limpieza-camiones/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/limpieza-camiones/${id}/archivos/${aid}`),
};

// ─────────────────────────── Decoración ───────────────────────────

export const decoracionApi = {
  list: (params?: any) => get<DecoracionEvento[]>(`${BASE}/decoracion`, params),
  get: (id: number) => get<DecoracionEvento>(`${BASE}/decoracion/${id}`),
  create: (body: Partial<DecoracionEvento>) => post<DecoracionEvento>(`${BASE}/decoracion`, body),
  update: (id: number, body: Partial<DecoracionEvento>) =>
    put<DecoracionEvento>(`${BASE}/decoracion/${id}`, body),
  remove: (id: number) => del(`${BASE}/decoracion/${id}`),
  addPersonal: (id: number, body: Partial<DecoracionPersonal>) =>
    post(`${BASE}/decoracion/${id}/personal`, body),
  removePersonal: (id: number, pid: number) => del(`${BASE}/decoracion/${id}/personal/${pid}`),
  addActividad: (id: number, body: Partial<DecoracionActividad>) =>
    post(`${BASE}/decoracion/${id}/actividades`, body),
  updateActividad: (id: number, actId: number, body: Partial<DecoracionActividad>) =>
    put(`${BASE}/decoracion/${id}/actividades/${actId}`, body),
  removeActividad: (id: number, actId: number) => del(`${BASE}/decoracion/${id}/actividades/${actId}`),
  addArchivo: (id: number, fd: FormData) => postForm(`${BASE}/decoracion/${id}/archivos`, fd),
  removeArchivo: (id: number, aid: number) => del(`${BASE}/decoracion/${id}/archivos/${aid}`),
};

// ─────────────────────────── Bitácora ───────────────────────────

export const bitacoraApi = {
  list: (params?: any) => get<Bitacora[]>(`${BASE}/bitacora`, params),
  get: (id: number) => get<Bitacora>(`${BASE}/bitacora/${id}`),
  exportUrl: (format: 'excel' | 'pdf', params?: Record<string, any>) => {
    const qs = new URLSearchParams({ format, ...(params || {}) }).toString();
    return `${axiosInstance.defaults.baseURL}${BASE}/bitacora/export?${qs}`;
  },
  reportePdf: (params?: any) => getBlob(`${BASE}/bitacora/reporte/pdf`, params),
};

// ─────────────────────────── Catálogos ───────────────────────────

export const catalogosApi = {
  tiposFaena: (params?: any) => get<CatalogoItem[]>(`${BASE}/catalogos/tipo-faena`, params),
  createTipoFaena: (body: Partial<CatalogoItem>) => post(`${BASE}/catalogos/tipo-faena`, body),
  updateTipoFaena: (id: number, body: Partial<CatalogoItem>) =>
    put(`${BASE}/catalogos/tipo-faena/${id}`, body),
  removeTipoFaena: (id: number) => del(`${BASE}/catalogos/tipo-faena/${id}`),
  categoriasHerramienta: (params?: any) =>
    get<CatalogoItem[]>(`${BASE}/catalogos/categoria-herramienta`, params),
  createCategoriaHerramienta: (body: Partial<CatalogoItem>) =>
    post(`${BASE}/catalogos/categoria-herramienta`, body),
  updateCategoriaHerramienta: (id: number, body: Partial<CatalogoItem>) =>
    put(`${BASE}/catalogos/categoria-herramienta/${id}`, body),
  removeCategoriaHerramienta: (id: number) => del(`${BASE}/catalogos/categoria-herramienta/${id}`),
};

// ─────────────────────────── Localidades (catálogo compartido) ───────────────────────────

export interface Localidad {
  id: number;
  descripcion: string;
}

export const localidadesApi = {
  list: () => get<Localidad[]>('/cLocalidadRoutes'),
};

export const TIPOS_VIALIDAD = ['Calle', 'Avenida', 'Carretera', 'Privada', 'Callejón', 'Camino'];
